import { DateFieldUI } from '@axo/form/feature/form-input/DateField/DateFieldUI';
import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import { loan_application } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { useWatch } from 'react-hook-form';
import {
  formatNumber,
  formatSpacedNumber,
} from '../../../../../../utils/format';
import { useESkatVisibility } from '../../../../ESkat';
import type { EmploymentFormGroupData } from '../../../FormData.dk.types';
import { employmentOptions, radioOptions } from '../../../config/globals';
import { requiredField } from '../../shared-config';
import {
  companyOrganizationNumberFieldOption,
  extraIncomeMonthlyFieldOption,
  grossIncomeMonthlyFieldOption,
  netIncomeMonthlyFieldOption,
} from './config';

export function EmploymentFormGroup() {
  const { t } = useTranslation();

  const employmentStatus = useWatch({
    name: 'LoanApplication/EmploymentStatus',
  });
  const hasExtraIncome =
    useWatch({ name: 'Frontend/HasExtraIncome' }) === 'Yes';

  const filledByESkat = useESkatVisibility('EmploymentStatus');

  return (
    <>
      {!filledByESkat && (
        <DropdownFieldUI<EmploymentFormGroupData>
          name="LoanApplication/EmploymentStatus"
          label={t('EmploymentStatus.label')}
          placeholder={t('placeholder')}
          data-testid="employment-dropdown"
          options={employmentOptions}
          registerOptions={requiredField}
        />
      )}

      {(employmentStatus || filledByESkat) && (
        <IncomeSection employmentStatus={employmentStatus} />
      )}

      <RadioFieldUI<EmploymentFormGroupData>
        name="Frontend/HasExtraIncome"
        label={t('HasExtraIncome.label')}
        data-testid="has-extra-income-radio"
        radioStyle="both"
        options={radioOptions}
        registerOptions={requiredField}
      />

      {hasExtraIncome && (
        <NumberFieldUI<EmploymentFormGroupData>
          name="LoanApplication/ExtraIncomeMonthly"
          label={t('ExtraIncomeMonthly.label')}
          data-testid="extra-income-input"
          format={formatSpacedNumber}
          suffix={t('suffix.currency')}
          {...extraIncomeMonthlyFieldOption}
        />
      )}
    </>
  );
}

type IncomeSectionProps = {
  employmentStatus: loan_application.EmploymentStatus;
};

const IncomeSection = ({ employmentStatus }: IncomeSectionProps) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const [
    sinceFilledByESkat,
    crvFilledByESkat,
    grossFilledByESkat,
    netFilledByESkat,
  ] = useESkatVisibility([
    'EmploymentStatusSince',
    'CompanyOrganizationNumber',
    'GrossIncomeMonthly',
    'NetIncomeMonthly',
  ]);

  return (
    <>
      {!sinceFilledByESkat && (
        <DateFieldUI<EmploymentFormGroupData>
          name="LoanApplication/EmploymentStatusSince"
          label={t('EmploymentStatusSince.label', {
            employmentStatus: t(`EmploymentStatus.${employmentStatus}`),
          })}
          data-testid="employment-status-since-dropdown"
          showYearOnly={true}
          minYear={currentYear - 10}
          maxYear={currentYear}
          disableDatesInFuture
          minYearSuffix={t('EmploymentStatusSince.year_suffix')}
          yearPlaceholder={t('placeholder')}
          latestYearAtTop={true}
          i18n="da-DK"
          rules={requiredField}
        />
      )}

      {employmentStatus === loan_application.EmploymentStatus.Entrepreneur &&
        !crvFilledByESkat && (
          <NumberFieldUI<EmploymentFormGroupData>
            name="LoanApplication/CompanyOrganizationNumber"
            label={t('CompanyOrganizationNumber.label')}
            data-testid="cvr-input"
            format={formatNumber}
            {...companyOrganizationNumberFieldOption}
          />
        )}

      {!grossFilledByESkat && (
        <NumberFieldUI<EmploymentFormGroupData>
          name="Frontend/GrossIncomeMonthly"
          label={t('GrossIncomeMonthly.label')}
          data-testid="gross-income-input"
          format={formatSpacedNumber}
          suffix={t('suffix.currency')}
          {...grossIncomeMonthlyFieldOption}
        />
      )}

      {!netFilledByESkat && (
        <NumberFieldUI<EmploymentFormGroupData>
          name="LoanApplication/NetIncomeMonthly"
          label={t('NetIncomeMonthly.label')}
          data-testid="net-income-input"
          format={formatSpacedNumber}
          suffix={t('suffix.currency')}
          {...netIncomeMonthlyFieldOption}
        />
      )}
    </>
  );
};
